import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UltimateParentFilters {
  retailerSegment: string | string[] | null;
  opportunitiesMin: number;
  opportunitiesMax: number;
  retailerSitesMin: number;
  retailerSitesMax: number;
}

// Column visibility defaults
const DEFAULT_COLUMNS = {
  brandName: true,
  totalOpp: true,
  purchek: true,
  cartContainment: true,
  cartManager: true,
  subscription: true,
  faceFirst: true,
  estCarts: true,
  estimatedRevenue: true,
} as const;

// Default filter values
const DEFAULT_FILTERS: UltimateParentFilters = {
  retailerSegment: null,
  opportunitiesMin: 0,
  opportunitiesMax: 999999999,
  retailerSitesMin: 0,
  retailerSitesMax: 999999999,
};

// Default state values - single source of truth
const INITIAL_STATE = {
  offset: 0,
  pageSize: 50,
  sortBy: 'Total Carts',
  searchInput: '',
  searchQuery: '',
  visibleColumns: DEFAULT_COLUMNS,
  filters: DEFAULT_FILTERS,
} as const;

export interface OpportunityState {
  // Pagination state
  offset: number;
  pageSize: number;
  sortBy: string;
  searchInput: string;
  searchQuery: string;
  visibleColumns: Record<string, boolean>;
  filters: UltimateParentFilters;

  // Actions
  setOffset: (offset: number) => void;
  setPageSize: (pageSize: number) => void;
  setSortBy: (sortBy: string) => void;
  setSearchInput: (searchInput: string) => void;
  setSearchQuery: (searchQuery: string) => void;
  setVisibleColumns: (visibleColumns: Record<string, boolean>) => void;
  toggleColumn: (columnId: string) => void;
  resetColumns: () => void;
  resetPagination: () => void;
  setFilters: (filters: UltimateParentFilters) => void;
  resetFilters: () => void;
}

const useOpportunityStore = create<OpportunityState>()(
  persist(
    (set) => ({
      // Default state
      ...INITIAL_STATE,

      // Pagination & Search Actions
      setOffset: (offset: number) => set({ offset }),
      setPageSize: (pageSize: number) => set({ pageSize, offset: 0 }),
      setSortBy: (sortBy: string) => set({ sortBy, offset: 0 }),
      setSearchInput: (searchInput: string) => set({ searchInput }),
      setSearchQuery: (searchQuery: string) => set({ searchQuery, offset: 0 }),

      // Column Visibility Actions
      setVisibleColumns: (visibleColumns: Record<string, boolean>) =>
        set({ visibleColumns }),
      toggleColumn: (columnId: string) =>
        set((state) => ({
          visibleColumns: {
            ...state.visibleColumns,
            [columnId]: !state.visibleColumns[columnId],
          },
        })),
      resetColumns: () => set({ visibleColumns: DEFAULT_COLUMNS }),

      // Filter Actions
      setFilters: (filters: UltimateParentFilters) => set({ filters, offset: 0 }),
      resetFilters: () => set({ filters: DEFAULT_FILTERS, offset: 0 }),

      // Reset to defaults
      resetPagination: () => set(INITIAL_STATE),
    }),
    {
      name: 'opportunity-storage',
      partialize: (state) => ({
        offset: state.offset,
        pageSize: state.pageSize,
        sortBy: state.sortBy,
        searchInput: state.searchInput,
        searchQuery: state.searchQuery,
        visibleColumns: state.visibleColumns,
        filters: state.filters,
      }),
    }
  )
);

export default useOpportunityStore;
